import { api } from '../../core/network/api';

/**
 * Session keepalive — rotates the refresh token while the player is actually here.
 *
 * Product rule (CU-86cb9ynr4): a closed tab must stop being a live session
 * within 10 minutes. That is enforced server-side by `JWT_REFRESH_TTL=10m` — a
 * tab that is gone issues no requests, so the refresh token simply expires and
 * the next visit lands on /login. No cookie surgery required.
 *
 * The catch is the player who IS here but happens not to hit the API: reading a
 * screen, moving the mouse, deciding. `installIdleLogout` counts them as active
 * (pointer movement bumps its timer), but their refresh token would still die at
 * the 10-minute mark and drop them mid-session.
 *
 * So: while activity is being seen, ping /auth/refresh every 4 minutes. Each
 * rotation resets the 10-minute clock. Stop pinging the moment activity stops,
 * which hands the expiry decision straight back to the server.
 *
 * 4 minutes is chosen against `JWT_ACCESS_TTL=5m` — the ping lands before the
 * access token expires, so the player never pays for a 401-and-retry mid-action.
 */
const PING_EVERY_MS = 4 * 60 * 1000;

/** No ping unless the player did something within this window. */
const ACTIVITY_WINDOW_MS = PING_EVERY_MS + 60_000;

const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

/** Start keeping the session warm; returns a teardown. */
export function installSessionKeepalive(intervalMs = PING_EVERY_MS): () => void {
  let lastActivity = Date.now();
  let inFlight = false;

  const bump = () => { lastActivity = Date.now(); };

  const ping = () => {
    // A hidden tab's timers are throttled, so this can fire late — the activity
    // window, not the interval, is what decides whether the player is still here.
    if (inFlight || document.hidden) return;
    if (Date.now() - lastActivity > ACTIVITY_WINDOW_MS) return;
    inFlight = true;
    // A failure here is not ours to handle: the axios interceptor already tears
    // the session down when the refresh token is genuinely gone.
    api.post('/auth/refresh').catch(() => null).finally(() => { inFlight = false; });
  };

  ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));
  const timer = window.setInterval(ping, intervalMs);

  return function teardown() {
    ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, bump));
    window.clearInterval(timer);
  };
}
